import { useState } from 'react';
import { fmtMoney, fmtNumber } from './format';
import { c, t, sp, tbl } from '../../theme';

// Parts totals by what the part IS — Engine, Brake System, Transmission.
//
// Not to be confused with the workbook's reporting categories (Aged 6-12,
// Aged +12, Non-Stock 45, Critical Spare), which the rest of the board calls
// "categories" too. Everything user-visible here says "Part Category" for that
// reason; the two answer different questions and never sum to each other.
//
// There are ~90 of these and the distribution has a long tail: the top 15 carry
// most of the value, the rest are a few thousand dollars each. The tail is
// folded into one row rather than cut, because a total that silently omits $180k
// of small categories is one nobody can reconcile against the KPI tile.
const DEFAULT_VISIBLE = 15;

// Part Lines counts rows; Distinct Parts counts part numbers. They are both
// here because the gap between them is real information — but only Part Lines
// adds up down the column. One part number can carry two part categories
// across its locations, so it is counted once in each; summing Distinct Parts
// overstates the catalogue. The fold row shows an em dash for that column
// rather than a total that would be wrong.
const COLUMNS = [
  { key: 'part_category',       label: 'Part Category',  align: 'left'  },
  { key: 'item_count',          label: 'Part Lines',     align: 'right' },
  { key: 'distinct_part_count', label: 'Distinct Parts', align: 'right' },
  { key: 'value',               label: 'Value',          align: 'right' },
  { key: 'over_max_value',      label: 'Over Max $',     align: 'right' },
  { key: 'aged_value',          label: 'Idle 12+',       align: 'right' },
  { key: 'aged_pct',            label: 'Idle %',         align: 'right' },
  { key: 'under_min_value',     label: 'Below Min $',    align: 'right' },
];

export default function PartCategoryTable({ rows = [], onPick }) {
  const [sortBy,  setSortBy]  = useState({ key: 'value', dir: 'desc' });
  const [showAll, setShowAll] = useState(false);

  if (!rows.length) {
    return <div style={s.empty}>No part categories in this snapshot.</div>;
  }

  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);

  // Derived columns are computed before sorting so the two cannot disagree —
  // sorting on a percentage the table recomputes at render time is how a list
  // ends up ordered by something other than what it shows.
  const enriched = rows.map((row) => ({
    ...row,
    aged_pct: Number(row.value) ? (Number(row.aged_value || 0) / Number(row.value)) * 100 : 0,
  }));

  const sorted = [...enriched].sort((a, b) => {
    const dir = sortBy.dir === 'asc' ? 1 : -1;
    if (sortBy.key === 'part_category') {
      return dir * String(a.part_category).localeCompare(String(b.part_category));
    }
    return dir * (Number(a[sortBy.key] || 0) - Number(b[sortBy.key] || 0));
  });

  const visible = showAll ? sorted : sorted.slice(0, DEFAULT_VISIBLE);
  const hidden  = sorted.slice(visible.length);
  const tail    = hidden.reduce(
    (acc, row) => ({
      item_count:      acc.item_count + Number(row.item_count || 0),
      value:           acc.value + Number(row.value || 0),
      over_max_value:  acc.over_max_value + Number(row.over_max_value || 0),
      aged_value:      acc.aged_value + Number(row.aged_value || 0),
      under_min_value: acc.under_min_value + Number(row.under_min_value || 0),
    }),
    // distinct_part_count is deliberately absent: it does not sum. See COLUMNS.
    { item_count: 0, value: 0, over_max_value: 0, aged_value: 0, under_min_value: 0 }
  );

  function head(col) {
    const active = sortBy.key === col.key;
    return (
      <th key={col.key} style={col.align === 'right' ? s.thR : s.th}>
        <button
          style={{ ...s.sortBtn, color: active ? c.navy : c.textMuted }}
          onClick={() => setSortBy({ key: col.key, dir: active && sortBy.dir === 'desc' ? 'asc' : 'desc' })}
        >
          {col.label}
          <span style={s.sortCaret}>{active ? (sortBy.dir === 'desc' ? '▾' : '▴') : ''}</span>
        </button>
      </th>
    );
  }

  return (
    <>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr>{COLUMNS.map(head)}</tr></thead>
          <tbody>
            {visible.map((row) => {
              const agedPct = Math.round(row.aged_pct);
              return (
                <tr key={row.part_category} style={s.tr}>
                  <td style={s.td}>
                    {onPick ? (
                      <button style={s.linkBtn} onClick={() => onPick(row.part_category)}>
                        {row.part_category}
                      </button>
                    ) : row.part_category}
                  </td>
                  <td style={s.tdR}>{fmtNumber(row.item_count)}</td>
                  <td style={s.tdR}>{fmtNumber(row.distinct_part_count)}</td>
                  <td style={s.tdR}>{fmtMoney(row.value)}</td>
                  <td style={s.tdR}>{row.over_max_value ? fmtMoney(row.over_max_value) : '—'}</td>
                  <td style={s.tdR}>{row.aged_value ? fmtMoney(row.aged_value) : '—'}</td>
                  <td style={{ ...s.tdR, color: agedPct >= 50 ? c.warningText : c.textStrong }}>
                    {agedPct}%
                  </td>
                  <td style={s.tdR}>{row.under_min_value ? fmtMoney(row.under_min_value) : '—'}</td>
                </tr>
              );
            })}

            {!!hidden.length && (
              // The fold row is the natural place to ask "which ones are those",
              // so it is the control rather than a static summary — the footer
              // link stays as the discoverable duplicate for anyone who has
              // already scrolled past the row.
              <tr
                style={s.trTailClickable}
                onClick={() => setShowAll(true)}
              >
                <td style={s.tdTail}>
                  <button style={s.tailBtn} aria-expanded={false}>
                    {hidden.length} smaller categories — show them
                  </button>
                </td>
                <td style={s.tdR}>{fmtNumber(tail.item_count)}</td>
                {/* Not a total — distinct parts overlap across categories. */}
                <td style={{ ...s.tdR, color: c.textFaint }} title="Distinct parts cannot be summed across categories — one part number can appear in more than one.">—</td>
                <td style={s.tdR}>{fmtMoney(tail.value)}</td>
                <td style={s.tdR}>{tail.over_max_value ? fmtMoney(tail.over_max_value) : '—'}</td>
                <td style={s.tdR}>{tail.aged_value ? fmtMoney(tail.aged_value) : '—'}</td>
                <td style={s.tdR}>
                  {tail.value ? `${Math.round((tail.aged_value / tail.value) * 100)}%` : '—'}
                </td>
                <td style={s.tdR}>{tail.under_min_value ? fmtMoney(tail.under_min_value) : '—'}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={s.foot}>
        <span>
          {fmtNumber(rows.length)} part categories · {fmtMoney(total)} total
          {' '}· distinct parts are counted within each category and do not sum
        </span>
        {rows.length > DEFAULT_VISIBLE && (
          <button style={s.moreBtn} onClick={() => setShowAll((v) => !v)}>
            {showAll ? `Show top ${DEFAULT_VISIBLE}` : `Show all ${fmtNumber(rows.length)}`}
          </button>
        )}
      </div>
    </>
  );
}

const s = {
  scroll: { overflowX: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { ...tbl.th,  whiteSpace: 'nowrap' },
  thR:    { ...tbl.thR, whiteSpace: 'nowrap' },
  tr:     { ...tbl.tr },
  td:     { ...tbl.td,  whiteSpace: 'nowrap' },
  tdR:    { ...tbl.tdR },

  trTailClickable: { ...tbl.tr, background: c.surfaceSunk, cursor: 'pointer' },
  tailBtn: {
    background: 'none', border: 'none', padding: 0, font: 'inherit',
    color: c.navy, cursor: 'pointer', fontWeight: 600, fontStyle: 'normal',
  },
  tdTail:  { ...tbl.td, whiteSpace: 'nowrap', color: c.textMuted, fontStyle: 'italic' },

  sortBtn: {
    background: 'none', border: 'none', padding: 0, font: 'inherit',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: sp[1],
  },
  sortCaret: { fontSize: '9px', width: '8px', display: 'inline-block' },

  linkBtn: {
    background: 'none', border: 'none', padding: 0, font: 'inherit',
    color: c.navy, cursor: 'pointer', textAlign: 'left',
  },

  foot: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: sp[3], padding: `${sp[2]} ${sp[4]}`, ...t.micro, color: c.textFaint,
    borderTop: `1px solid ${c.border}`, background: c.surfaceSunk,
  },
  moreBtn: {
    background: 'none', border: 'none', padding: 0, font: 'inherit',
    color: c.navy, cursor: 'pointer', fontWeight: 600,
  },
  empty: { padding: sp[8], textAlign: 'center', ...t.bodySm, color: c.textFaint },
};
